/**
 * ADR-0185 Addendum 4 — single path: the Optimize landing is ONE list for
 * every role, and a model's home (`/app/optimize/:modelId`) opens on its
 * Board tab with a secondary informational Model tab (`?tab=model`).
 *
 * These are the pure routing decisions that follow: where the retired
 * `/app/schedule-board` and `/app/optimize/:modelId/board` addresses land,
 * where a viewer lands when they click Optimize, and which tab a model-home
 * URL names. They live here (not inline in the routes) so the redirects and
 * the viewer fast path are testable without a DOM.
 */

/** The active models the caller can read, as served on the bootstrap fetch. */
export type OptimizeModelRef = { id: string; name: string };

/** The model's home — opens on its Board tab. */
export function modelHomePath(modelId: string): string {
  return `/app/optimize/${encodeURIComponent(modelId)}`;
}

/** The model home's informational Model tab. */
export function modelTabPath(modelId: string): string {
  return `${modelHomePath(modelId)}?tab=model`;
}

/** The Optimize landing — one list, every role. */
export function optimizePath(): string {
  return "/app/optimize";
}

export type ModelHomeTab = "board" | "nextup" | "model";

/** The model home's tab from its URL: Board is the default (no param). */
export function modelHomeTabFromSearch(raw: string | null): ModelHomeTab {
  if (raw === "model") return "model";
  if (raw === "next-up") return "nextup";
  return "board";
}

/**
 * Creating, configuring and operating models stays builder/admin: the
 * "+ New model" gesture and the Model tab's quiet admin strip.
 */
export function canManageModels(entitlement: string | undefined): boolean {
  return entitlement === "builder" || entitlement === "admin";
}

/**
 * A `?tab=` search on the landing names tabs that no longer exist (the
 * Models/Boards split is retired) — send the caller to the plain list.
 */
export function optimizeListRedirect(search: URLSearchParams): string | null {
  return search.has("tab") ? optimizePath() : null;
}

/**
 * The viewer fast path (owner-ruled): a caller without model-manage authority
 * who has exactly one active model lands STRAIGHT on that model's home — its
 * Board tab, one click from nav. With several models they get the list; with
 * none, the list renders its empty state.
 *
 * Returns null when the landing itself should render.
 */
export function optimizeLandingRedirect(
  entitlement: string | undefined,
  models: OptimizeModelRef[]
): string | null {
  if (canManageModels(entitlement)) return null;
  return models.length === 1 ? modelHomePath(models[0]!.id) : null;
}

/**
 * The retired `/app/schedule-board` URL no longer names anything — the board
 * is the model home's default tab. Exactly one active model goes to its home;
 * several go to the list so the caller picks; none goes home.
 */
export function legacyBoardRedirect(models: OptimizeModelRef[]): string {
  if (models.length === 1) return modelHomePath(models[0]!.id);
  if (models.length > 1) return optimizePath();
  return "/app";
}
